import React, { useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Alert, Button, ButtonGroup, Input, Label } from 'reactstrap';
import { QRCodeSVG } from 'qrcode.react';
import EIVHeader from '../../EIVHeader';
import { CSS_CLASSNAME_PANEL } from '../../EIVCommons';
import { setScreenVeranstaltung, setScreenDruckansicht, druckVarianten } from '../../store/appNavigation/actions';
import { fobiCore } from '../../FobiCore';
import { isShowTeilnahmedatum } from '../TeilnehmerMelden/teilnahmedatum';
import { MAX_COUNT_PUNKTEMELDUNGEN } from '../../store/punkteMeldung/fetchPunktemeldungen';
import { baueAnwesenheitsliste, baueProtokollliste, baueVeranstaltungsort, kuerzeEFN,
	zaehleOhneBeschriftung, zuCsv, alsJaNein } from './druckdaten';

const STANDARD_NACHTRAGSZEILEN = 10;
const MAX_NACHTRAGSZEILEN = 99;
const STELLEN_EFN = 15;

/**
 * Der QR traegt die VNR - und nur die. Beim Hinzufuegen einer Veranstaltung scannt die App
 * VNR und Kennwort gemeinsam aus einem Kammer-Barcode; dasselbe hier zu drucken waere
 * naheliegend und falsch. Das Blatt liegt am Einlasstisch offen herum, und wer das Kennwort
 * abfotografiert, kann Punkte auf diese Veranstaltung melden.
 */
const DruckKopf = (props) => {
	const { veranstaltung, titel } = props;
	return (
		<div className="druck-kopf mb-3">
			<div className="druck-kopf-titel">
				<div className="h4 mb-0">{titel}</div>
				<div className="small">zur Veranstaltung</div>
				<div className="druck-thema mt-1">{veranstaltung.thema}</div>
				{veranstaltung.unterthema && <div>{veranstaltung.unterthema}</div>}
			</div>
			<div className="druck-kopf-qr">
				<QRCodeSVG value={veranstaltung.vnr} size={80} level="M" />
			</div>
		</div>
	);
}

const DruckEckdaten = (props) => {
	const { veranstaltung, anzahl } = props;
	const beginn = moment(veranstaltung.beginn).format('DD.MM.YYYY HH:mm');
	const ende = moment(veranstaltung.ende).format('DD.MM.YYYY HH:mm');
	const ortszeilen = baueVeranstaltungsort(veranstaltung);
	return (
		<div className="druck-eckdaten mb-3">
			<div>vom: {beginn} – {ende}</div>
			{ortszeilen.length > 0 &&
				<div className="druck-ort">
					Ort: {ortszeilen.map((zeile, nr) => <span key={nr}>{nr > 0 && <br />}{zeile}</span>)}
				</div>
			}
			<div>VNR: {veranstaltung.vnr} Kat. ({veranstaltung.kategorie})</div>
			<div>
				Teilnahme: {veranstaltung.punkte_basis} Pkt.
				{fobiCore.isShowLernerfolg(veranstaltung) && <> · Lernerfolgskontrolle: {veranstaltung.punkte_lernerfolg} Pkt.</>}
				{' '}· Zeilen: {anzahl} · Druck: {moment().format('DD.MM.YYYY HH:mm')}
			</div>
		</div>
	);
}

/**
 * Steht als erste Zeile im thead und wiederholt sich damit auf jeder gedruckten Seite. Ein
 * per position:fixed gesetzter Seitenfuss taete das in Chrome und Firefox auch, in Safari
 * nicht - die Kopfgruppe der Tabelle ist der Mechanismus, der ueberall traegt.
 */
const Laufzeile = (props) => (
	<tr className="druck-laufzeile">
		<th colSpan={props.spalten}>
			VNR {props.veranstaltung.vnr} · {props.veranstaltung.thema} · {moment().format('DD.MM.YYYY')}
		</th>
	</tr>
);

const EfnRaster = () => (
	<div className="efn-raster" aria-hidden="true">
		{Array.from({ length: STELLEN_EFN }).map((_, stelle) => <span key={stelle} className="efn-kaestchen" />)}
	</div>
);

const TabelleErfasste = (props) => {
	const { zeilen, veranstaltung, showTeilnahmedatum } = props;
	const spalten = 4 + (showTeilnahmedatum ? 1 : 0);
	return (
		<table className="druck-tabelle mb-3">
			<thead>
				<Laufzeile spalten={spalten} veranstaltung={veranstaltung} />
				<tr>
					<th className="druck-spalte-nr">Nr.</th>
					<th>Name / Notiz</th>
					<th className="druck-spalte-efn">EFN</th>
					{showTeilnahmedatum && <th>Teilnahme&shy;datum</th>}
					<th className="druck-spalte-ref">Ref./Mod./ w. Leitung</th>
					<th className="druck-spalte-unterschrift">Unterschrift Teilnehmer</th>
				</tr>
			</thead>
			<tbody>
				{zeilen.map((zeile, index) => (
					<tr key={zeile.efn} className="druck-zeile-erfasst">
						<td className="druck-spalte-nr">{index + 1}</td>
						<td>
							{zeile.text}
						</td>
						<td className="druck-spalte-efn">{kuerzeEFN(zeile.efn)}</td>
						{showTeilnahmedatum && <td>{zeile.teilnahmedatum}</td>}
						<td className="druck-spalte-ref">{zeile.punkteReferent > 0 ? zeile.punkteReferent : ""}</td>
						<td className="druck-spalte-unterschrift"></td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

/**
 * Keine eigene Namensspalte: in der Zelle fuer den Aufkleber ist neben dem Etikett bzw. ueber
 * dem Kaestchenraster genug Platz, den Namen dazuzuschreiben. Das spart die Spalte und laesst
 * der Unterschrift mehr Breite.
 */
const TabelleNachtraege = (props) => {
	const { anzahl, beginnendeNummer, veranstaltung } = props;
	return (
		<table className="druck-tabelle">
			<thead>
				<Laufzeile spalten={4} veranstaltung={veranstaltung} />
				<tr>
					<th className="druck-spalte-nr">Nr.</th>
					<th className="druck-spalte-aufkleber">
						Barcode mit EFN aufkleben – oder EFN und Name eintragen
					</th>
					<th className="druck-spalte-ref">Ref./Mod./ w. Leitung</th>
					<th className="druck-spalte-unterschrift">Unterschrift Teilnehmer</th>
				</tr>
			</thead>
			<tbody>
				{Array.from({ length: anzahl }).map((_, index) => (
					<tr key={index} className="druck-zeile-nachtrag">
						<td className="druck-spalte-nr">{beginnendeNummer + index}</td>
						<td className="druck-spalte-aufkleber"><EfnRaster /></td>
						<td className="druck-spalte-ref"></td>
						<td className="druck-spalte-unterschrift"></td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

const TabelleProtokoll = (props) => {
	const { zeilen, veranstaltung, showTeilnahmedatum, showLernerfolg, showBeschriftung } = props;
	// Die Punktzahl steht auch in den Eckdaten - die stehen aber nur auf Seite 1, waehrend
	// sich der Spaltenkopf auf jeder Seite wiederholt.
	const spalten = 5 + (showBeschriftung ? 1 : 0) + (showTeilnahmedatum ? 1 : 0) + (showLernerfolg ? 1 : 0);
	return (
		<table className="druck-tabelle">
			<thead>
				<Laufzeile spalten={spalten} veranstaltung={veranstaltung} />
				<tr>
					<th className="druck-spalte-nr">Nr.</th>
					<th className="druck-spalte-efn">EFN</th>
					{showBeschriftung && <th>Name / Notiz</th>}
					{showTeilnahmedatum && <th>Teilnahme&shy;datum</th>}
					<th>Teilnahme ({veranstaltung.punkte_basis} Pkt.)</th>
					{showLernerfolg && <th>Lernerfolg ({veranstaltung.punkte_lernerfolg} Pkt.)</th>}
					<th className="druck-spalte-ref">Ref./Mod./ w. Leitung</th>
					<th>Meldedatum</th>
				</tr>
			</thead>
			<tbody>
				{zeilen.map((zeile, index) => (
					<tr key={zeile.efn}>
						<td className="druck-spalte-nr">{index + 1}</td>
						<td className="druck-spalte-efn">{zeile.efn}</td>
						{showBeschriftung &&
							<td>
								{zeile.text}
							</td>
						}
						{showTeilnahmedatum && <td>{zeile.teilnahmedatum}</td>}
						<td>{alsJaNein(zeile.punkteBasis)}</td>
						{showLernerfolg && <td>{alsJaNein(zeile.punkteLernerfolg)}</td>}
						<td className="druck-spalte-ref">{zeile.punkteReferent > 0 ? zeile.punkteReferent : ""}</td>
						<td>{zeile.meldedatum}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

/**
 * Der Gegensatz zum roten Hinweis auf dem Papierformular der Kammer: dort steht, das
 * Einreichen der Liste sei gebuehrenpflichtig. Hier steht, warum sie gar nicht eingereicht
 * werden muss. Deshalb traegt dieses Blatt bewusst keine Kammeranschrift und kein Feld fuer
 * ein Aktenzeichen - es soll beim Veranstalter bleiben.
 */
const DruckFuss = (props) => (
	<div className="druck-fuss">
		<div className="d-flex justify-content-between align-items-end gap-3">
			<div>
				<div className="druck-unterschrift-linie"></div>
				<div>Datum, Unterschrift (wissenschaftliche Leitung)</div>
			</div>
			<div className="text-end" style={{ maxWidth: "95mm" }}>
				Die Punkte zu dieser Veranstaltung werden über den EIV gemeldet. Diese Liste verbleibt
				beim Veranstalter als eigener Nachweis und ist <b>nicht bei der Ärztekammer einzureichen</b>.
			</div>
		</div>
		{ props.showBeschriftung &&
			<div className="mt-2">
				Name / Notiz sind vom Veranstalter erfasste Angaben. Sie werden von keiner Ärztekammer
				geprüft und nicht an den EIV übertragen; gemeldet werden ausschließlich EFN, Teilnahme-
				und Lernerfolg-Kennzeichen, Ref/Mod/WL-Punkte und das Teilnahmedatum.
			</div>
		}
	</div>
);

const ladeCsvHerunter = (dateiname, inhalt) => {
	// Das BOM ist der Unterschied zwischen "Mörfelden" und "MÃ¶rfelden", sobald jemand die
	// Datei per Doppelklick in Excel oeffnet.
	const blob = new Blob(["\uFEFF" + inhalt], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = dateiname;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

/**
 * Der Export behaelt die Spalte "Name / Notiz" auch dann, wenn sie durchgehend leer ist und
 * auf dem Blatt entfaellt. Zusammengefuehrt werden die Exporte mehrerer Erfassungsgeraete in
 * Excel - dabei muessen alle Dateien dieselben Spalten in derselben Reihenfolge haben, sonst
 * verrutscht ausgerechnet die Datei, die die Namen nicht kennt.
 */
const csvSpalten = (istProtokoll, showTeilnahmedatum, showLernerfolg) => {
	const spalten = [
		{ titel: "EFN", wert: zeile => zeile.efn },
		{ titel: "Name / Notiz", wert: zeile => zeile.text }
	];
	if(showTeilnahmedatum) spalten.push({ titel: "Teilnahmedatum", wert: zeile => zeile.teilnahmedatum });
	spalten.push({ titel: "Teilnahme", wert: zeile => alsJaNein(zeile.punkteBasis) });
	if(showLernerfolg) spalten.push({ titel: "Lernerfolg", wert: zeile => alsJaNein(zeile.punkteLernerfolg) });
	spalten.push({ titel: "Ref/Mod/WL", wert: zeile => zeile.punkteReferent });
	if(istProtokoll) spalten.push({ titel: "Meldedatum", wert: zeile => zeile.meldedatum });
	return spalten;
};

const UnbekannteVNR = () => (
	<>
		<EIVHeader />
		<div className="container pt-2">
			<Alert color="warning">Zu dieser Veranstaltung liegen keine Daten vor.</Alert>
		</div>
	</>
);

const Druckansicht = (props) => {
	const { vnr, veranstaltung, variante, erfassteTeilnahmenOfVNR, teilnahmeMeldungenOfVNR,
		beschriftungenOfVNR, zurueck, wechsleVariante } = props;
	const [nachtragszeilen, setNachtragszeilen] = useState(STANDARD_NACHTRAGSZEILEN);
	if(veranstaltung === undefined) return <UnbekannteVNR />;

	const istProtokoll = variante === druckVarianten.meldeprotokoll;
	const showTeilnahmedatum = isShowTeilnahmedatum(veranstaltung);
	const showLernerfolg = fobiCore.isShowLernerfolg(veranstaltung);
	const zeilen = istProtokoll
		? baueProtokollliste(teilnahmeMeldungenOfVNR, beschriftungenOfVNR)
		: baueAnwesenheitsliste(erfassteTeilnahmenOfVNR, beschriftungenOfVNR);
	const ohneBeschriftung = zaehleOhneBeschriftung(zeilen);
	const alleOhneBeschriftung = zeilen.length > 0 && ohneBeschriftung === zeilen.length;
	// Auf dem Protokoll schreibt niemand mehr etwas - eine durchgehend leere Spalte ist dort
	// nur Breite, die EFN, Datum und Meldezeitpunkt besser gebrauchen koennen. Auf der
	// Teilnehmerliste bleibt sie stehen, auch wenn sie leer ist: dort ist sie die Zeile, in
	// die der Teilnehmer neben seiner Unterschrift den Namen schreibt, und der Fall "alle
	// leer" ist genau der, in dem das gebraucht wird - gescannt wurde nur der EFN-Barcode.
	const showBeschriftung = !(istProtokoll && alleOhneBeschriftung);
	const titel = istProtokoll ? "Meldeprotokoll" : "Teilnehmerliste";

	const exportiere = () => {
		const spalten = csvSpalten(istProtokoll, showTeilnahmedatum, showLernerfolg);
		const dateiname = `${istProtokoll ? "Meldeprotokoll" : "Teilnehmer"}_${vnr}_${moment().format('YYYY-MM-DD')}.csv`;
		ladeCsvHerunter(dateiname, zuCsv(zeilen, spalten));
	};

	return (
		<>
		<div className="d-print-none"><EIVHeader /></div>
		<div className="container bg-body-tertiary pt-2 pb-2">
			<div className={CSS_CLASSNAME_PANEL + " d-print-none"}>
				<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
					<Button outline color="secondary" onClick={() => zurueck(vnr)}>← Zurück zur Veranstaltung</Button>
					<ButtonGroup>
						<Button color="info" outline={istProtokoll}
							onClick={() => wechsleVariante(druckVarianten.anwesenheitsliste)}>Teilnehmerliste</Button>
						<Button color="info" outline={!istProtokoll}
							onClick={() => wechsleVariante(druckVarianten.meldeprotokoll)}>Meldeprotokoll</Button>
					</ButtonGroup>
					<div className="d-flex gap-2">
						<Button color="secondary" outline onClick={exportiere}>Als CSV speichern</Button>
						<Button color="primary" onClick={() => window.print()}>Drucken / als PDF sichern</Button>
					</div>
				</div>
				{ !istProtokoll &&
					<div className="row g-2">
						<div className="col-12 col-md-4">
							<Label for="druckNachtraege" className="form-label small mb-1">Nachtragszeilen</Label>
							<Input id="druckNachtraege" bsSize="sm" type="number" min={0} max={MAX_NACHTRAGSZEILEN}
								value={nachtragszeilen}
								onChange={(e) => setNachtragszeilen(Math.max(0, Math.min(MAX_NACHTRAGSZEILEN, Number(e.target.value) || 0)))} />
						</div>
					</div>
				}
				<p className="small text-body-secondary mt-3 mb-0">
					Seitenzahl sowie Kopf- und Fußzeile des Browsers stellen
					Sie im Druckdialog ein. Beim Öffnen der CSV-Datei in Excel bitte die EFN-Spalte als
					<i> Text</i> importieren, sonst wird aus der EFN eine gerundete Zahl.
				</p>
			</div>

			<div className="druck-blatt-rahmen">
			<div className="druck-blatt">
				<DruckKopf veranstaltung={veranstaltung} titel={titel} />
				<DruckEckdaten veranstaltung={veranstaltung} anzahl={zeilen.length} />

				{ istProtokoll ?
					<>
						{ zeilen.length === 0 ?
							<Alert color="" className="text-center">Es wurden noch keine Punkte gemeldet.</Alert> :
							<TabelleProtokoll zeilen={zeilen} veranstaltung={veranstaltung}
								showTeilnahmedatum={showTeilnahmedatum} showLernerfolg={showLernerfolg}
								showBeschriftung={showBeschriftung} />
						}
						{ zeilen.length >= MAX_COUNT_PUNKTEMELDUNGEN &&
							<p className="druck-fuss mt-2 mb-0">
								Es werden höchstens {MAX_COUNT_PUNKTEMELDUNGEN} Meldungen geladen und gedruckt.
							</p>
						}
						{ alleOhneBeschriftung &&
							<p className="druck-fuss mt-2 mb-0">
								Zu keiner der {zeilen.length} Meldungen liegt ein Name / eine Notiz vor – ohne Notiz
								oder auf einem anderen Gerät erfasst. Die Spalte entfällt deshalb; der CSV-Export
								enthält sie weiterhin.
							</p>
						}
						{ ohneBeschriftung > 0 && !alleOhneBeschriftung &&
							<p className="druck-fuss mt-2 mb-0">
								{ohneBeschriftung} von {zeilen.length} Zeilen ohne Name / Notiz – ohne Notiz oder
								auf einem anderen Gerät erfasst.
							</p>
						}
					</> :
					<>
						{ zeilen.length > 0 &&
							<>
								<TabelleErfasste zeilen={zeilen} veranstaltung={veranstaltung}
									showTeilnahmedatum={showTeilnahmedatum} />
								<p className="druck-fuss mt-0 mb-3">
									Auf diesem Gerät erfasst. Werden mehrere Geräte zur Erfassung eingesetzt,
									führt jedes seine eigene Liste.
								</p>
							</>
						}
						{ nachtragszeilen > 0 &&
							<>
								<div className="mb-1"><b>Nachträge</b> – Teilnehmende ohne erfasste EFN</div>
								<TabelleNachtraege anzahl={nachtragszeilen} beginnendeNummer={zeilen.length + 1}
									veranstaltung={veranstaltung} />
							</>
						}
					</>
				}

				<DruckFuss showBeschriftung={showBeschriftung} />
			</div>
			</div>
		</div>
		</>
	);
}

const mapStateToProps = (state) => {
	const vnr = state.appNavigation.selectedVNR;
	return {
		vnr,
		veranstaltung: state.veranstaltungen[vnr],
		variante: state.appNavigation.druckVariante,
		erfassteTeilnahmenOfVNR: state.punkteMeldung.erfassteTeilnahmen[vnr],
		teilnahmeMeldungenOfVNR: state.punkteMeldung.teilnahmeMeldungen[vnr],
		beschriftungenOfVNR: state.punkteMeldung.beschriftungen[vnr]
	};
}
const mapDispatchToProps = (dispatch) => ({
	zurueck: (vnr) => dispatch(setScreenVeranstaltung(vnr)),
	wechsleVariante: (variante) => dispatch(setScreenDruckansicht(variante))
});

export default connect(mapStateToProps, mapDispatchToProps)(Druckansicht);
